import { createSignal, For, createEffect, createMemo, onMount, onCleanup, Show } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { useNavigate } from "@solidjs/router";
import { Row, Button, Column } from "@lightningtv/solid-ui";
import gsap from "gsap";
import axios from "axios";
import { Buffer } from "buffer";


// Import your existing state and providers
import {
  playListPassword,
  playListUrl,
  playListUsername,
  setIsPlaying,
  playListName,
} from "../../../state";
import { createInfiniteScroll } from "../../components/pagination";
import moviesCategory from "../../api/providers/moviesCategory";
import livetvChannels from "../../api/providers/livetvChannels";
import moviesProvider from "../../api/providers/movies";
import LoadingIndicator from "../loading/loading";

// Import cache manager
import { cache, CacheKeys, CacheTTL } from "../cacheManager";
import { fixImageUrl } from "../../utils/fixImageUrl";
import SettingsPanel from "../settings/SettingsPanel";
import MoviesPanel from "../movies/MoviesPanel";
import ServicesPanel from "../services/ServicesPanel";
import SmartSearchPanel from "../search/SmartSearchPanel";
import { ChannelCard, MovieCard } from "./HomeCards";

// Helper function to fetch individual movie info WITH CACHING
async function fetchMovieInfo(movieId: string) {
  const cacheKey = CacheKeys.movieInfo(movieId);
  const cachedData = cache.get(cacheKey);

  if (cachedData) {
    console.log(`💾 Using cached movie info for ID: ${movieId}`);
    return cachedData;
  }

  const vpnStatus = localStorage.getItem("vpn");
  const proxyUrl = localStorage.getItem("proxy_url");

  let apiUrl = `${playListUrl()}/player_api.php?username=${playListUsername()}&password=${playListPassword()}&action=get_vod_info&vod_id=${movieId}`;

  console.log("=== MOVIE INFO API DEBUG ===");
  console.log("📍 Original API URL:", apiUrl);
  console.log("🔐 VPN Status:", vpnStatus);
  console.log("🌐 Proxy URL:", proxyUrl);

  if (vpnStatus === "on" && proxyUrl) {
    const bufferedUrl = Buffer.from(apiUrl, "utf-8").toString("base64");
    apiUrl = `${proxyUrl}${bufferedUrl}`;
    console.log("🔀 Modified API URL (via proxy):", apiUrl);
  }

  try {
    console.log(`🎬 Fetching movie info for ID: ${movieId}`);
    const response = await axios.get(apiUrl, { timeout: 8000 });
    console.log(`✅ Movie info response:`, response.data);

    cache.set(cacheKey, response.data, CacheTTL.movieInfo);
    return response.data;
  } catch (error) {
    console.error(`❌ Error fetching movie info for ID ${movieId}:`);
    console.error("Error details:", error);
    if (axios.isAxiosError(error)) {
      console.error("Response status:", error.response?.status);
      console.error("Response data:", error.response?.data);
      console.error("Error message:", error.message);
    }
    return null;
  }
}

// Helper function to format runtime
function formatRuntime(runtime: string | number) {
  if (!runtime) return "N/A";
  const minutes = typeof runtime === "string" ? parseInt(runtime) : runtime;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

// Splits a hero title into at most 2 lines (word-wrapped, ellipsis on overflow)
const HERO_TITLE_LINE_CHARS = 24;
function splitHeroTitle(rawTitle: string): string[] {
  const title = (rawTitle || "").trim();
  if (title.length <= HERO_TITLE_LINE_CHARS) return [title];

  const cut = (text: string, max: number) => {
    if (text.length <= max) return { line: text, rest: "" };
    const space = text.lastIndexOf(" ", max);
    const end = space > 0 ? space : max;
    return { line: text.substring(0, end).trim(), rest: text.substring(end).trim() };
  };

  const first = cut(title, HERO_TITLE_LINE_CHARS);
  if (!first.rest) return [first.line];

  const second = cut(first.rest, HERO_TITLE_LINE_CHARS);
  if (!second.rest) return [first.line, second.line];

  const trimmed = second.line.substring(0, HERO_TITLE_LINE_CHARS - 3).trim();
  return [first.line, trimmed + "..."];
}

const ResidentialHome = () => {
  const navigate = useNavigate();

  // State signals
  const [guestName, setGuestName] = createSignal("Guest");
  const [liveChannels, setLiveChannels] = createSignal<any[]>([]);
  const [movies, setMovies] = createSignal<any[]>([]);
  const [heroMovies, setHeroMovies] = createSignal<any[]>([]);
  const [heroSelectedIndex, setHeroSelectedIndex] = createSignal(0);
  const [topNavSelectedIndex, setTopNavSelectedIndex] = createSignal(0);
  const [isTopNavFocused, setIsTopNavFocused] = createSignal(true);
  const [isLoadingChannels, setIsLoadingChannels] = createSignal(true);
  const [isLoadingMovies, setIsLoadingMovies] = createSignal(true);
  const [isLoadingHero, setIsLoadingHero] = createSignal(true);
  const [firstMovieCategory, setFirstMovieCategory] = createSignal<any>(null);
  const [showLoading, setShowLoading] = createSignal(true);
  const [isNavigating, setIsNavigating] = createSignal(false);
  const [shouldHideLoading, setShouldHideLoading] = createSignal(false);
  const [dataLoadedFromCache, setDataLoadedFromCache] = createSignal(false);
  // Settings/Movies/Services/Search all shown inline under the header,
  // instead of navigating to their own full-page routes
  // (/userInfo, /movies, /services, /smartSearch)
  const [showSettings, setShowSettings] = createSignal(false);
  const [showMoviesPanel, setShowMoviesPanel] = createSignal(false);
  const [showServicesPanel, setShowServicesPanel] = createSignal(false);
  const [showSearchPanel, setShowSearchPanel] = createSignal(false);

  // ── Back-press-to-exit ────────────────────────────────────────────
  const [showExitToast, setShowExitToast] = createSignal(false);
  const [escapePressed, setEscapePressed] = createSignal(false);
  let exitToastTimer: ReturnType<typeof setTimeout> | null = null;

  const handleBackPress = () => {
    if (!escapePressed()) {
      setEscapePressed(true);
      setShowExitToast(true);
      if (exitToastTimer) clearTimeout(exitToastTimer);
      exitToastTimer = setTimeout(() => {
        setShowExitToast(false);
        setEscapePressed(false);
        exitToastTimer = null;
      }, 4000);
    } else {
      if (exitToastTimer) clearTimeout(exitToastTimer);
      setShowExitToast(false);
      setEscapePressed(false);
      // Call AndroidBridge.exitApp() - matches your bridge interface
      try { (window as any).AndroidBridge?.exitApp?.(); } catch (_) { }
    }
  };

  onCleanup(() => {
    if (exitToastTimer) clearTimeout(exitToastTimer);
  });
  // ─────────────────────────────────────────────────────────────────

  const DEBUG_API = false; // Toggle this to enable/disable debug logs

  // Then add this debug helper function
  const debugLog = (label: string, data: any) => {
    if (DEBUG_API) {
      console.log(`🔍 [DEBUG ${label}]`, data);
    }
  };


  // Refs
  let topNavRef: any;
  let settingsNavButtonRef: any;
  let moviesNavButtonRef: any;
  let servicesNavButtonRef: any;
  let searchNavButtonRef: any;
  // Imperative handles MoviesPanel/ServicesPanel/SmartSearchPanel attach a
  // focusEntry() to, so the header can send focus into whichever is open
  // (see the header's onDown below).
  const moviesPanelRef: { focusEntry?: () => void } = {};
  const servicesPanelRef: { focusEntry?: () => void } = {};
  const searchPanelRef: { focusEntry?: () => void } = {};
  let contentColumnRef: any;
  let heroRowRef: any;
  let channelsRowRef: any;
  let moviesRowRef: any;
  let mainContentRef: any;
  let loadingScreenRef: any;

  // Helper function for smooth navigation with fade out
  const handleNavigate = (path: string) => {
    if (isNavigating()) return;
    setIsNavigating(true);

    gsap.to(mainContentRef, {
      alpha: 0,
      duration: 0.25,
      ease: "power2.inOut",
      onComplete: () => {
        navigate(path);
        setIsNavigating(false);
      }
    });
  };

  const NAV_ITEMS = [
    {
      label: "Home",
      // Already on Home normally, so this was a no-op — but if an inline
      // panel (Settings/Movies/Services/Search) is open over it, pressing
      // Home should close it and reveal the Home content underneath.
      onEnter: () => {
        if (showSettings()) setShowSettings(false);
        if (showMoviesPanel()) setShowMoviesPanel(false);
        if (showServicesPanel()) setShowServicesPanel(false);
        if (showSearchPanel()) setShowSearchPanel(false);
      }
    },
    {
      label: "Live TV",
      onEnter: () => {
        const channels = liveChannels();
        if (channels && channels.length > 0) {
          const firstChannel = channels[0];
          const streamId = firstChannel.stream_id;

          console.log("📺 Playing first live channel:", firstChannel.name);

          if (streamId) {
            handleNavigate(`/livetv?num=${streamId}&isTrue=true&fromHome=true`);
            setIsPlaying(true);
          } else {
            console.error("❌ First channel has no stream_id");
            // handleNavigate("/channel-menu");
          }
        } else {
          console.warn("⚠️ No channels available, going to channel menu");
          // handleNavigate("/channel-menu");
        }
      }
    },
    {
      label: "Movies",
      // Shown inline below the header instead of navigating away — see
      // the MoviesPanel overlay near the end of this component's JSX.
      onEnter: () => {
        setShowServicesPanel(false);
        setShowSettings(false);
        setShowSearchPanel(false);
        setShowMoviesPanel(true);
      }
    },
    {
      label: "Services",
      onEnter: () => {
        setShowMoviesPanel(false);
        setShowSettings(false);
        setShowSearchPanel(false);
        setShowServicesPanel(true);
      }
    },
    {
      label: "Settings",
      // Shown inline below the header instead of navigating away — see
      // the SettingsPanel overlay near the end of this component's JSX.
      onEnter: () => {
        setShowMoviesPanel(false);
        setShowServicesPanel(false);
        setShowSearchPanel(false);
        setShowSettings(true);
      }
    },
  ];

  // Initialize guest name and GSAP animations
  onMount(() => {
    console.log("🏠 HomePage mounted");

    // Tell Android the home screen is rendering - restore WebView transparency
    try {
      if ((window as any).AndroidVideoPlayer?.notifyHomeReady) {
        (window as any).AndroidVideoPlayer.notifyHomeReady();
      }
    } catch (_) { }

    // === DEBUG: Check API Credentials ===
    debugLog("Playlist URL", playListUrl());
    debugLog("Username", playListUsername());
    debugLog("Password", playListPassword());
    debugLog("VPN Status", localStorage.getItem("vpn"));
    debugLog("Proxy URL", localStorage.getItem("proxy_url"));

    // Try to load from cache first
    loadCachedData();

    const name = playListName();
    if (name) {
      setGuestName(name);
    }

    // Set a maximum wait time (5 seconds)
    const maxWaitTime = 5000;
    const timeoutId = setTimeout(() => {
      console.warn("⏱️ Timeout reached, showing page anyway");
      setShouldHideLoading(true);
    }, maxWaitTime);

    // Watch for hero movies to be loaded OR timeout
    createEffect(() => {
      const heroReady = !isLoadingHero() && heroMovies().length > 0;
      const timeoutReached = shouldHideLoading();

      // Hide loading screen when EITHER hero is ready OR timeout is reached
      if (heroReady || timeoutReached) {
        clearTimeout(timeoutId); // Clear timeout if hero loaded first

        console.log(heroReady
          ? "✅ Hero movies loaded, hiding loading screen"
          : "⏱️ Timeout reached, hiding loading screen");

        const timeline = gsap.timeline();

        timeline.to(loadingScreenRef, {
          alpha: 0,
          duration: 0.3,
          ease: "power2.out",
          delay: 0.2,
          onComplete: () => {
            setShowLoading(false);
          }
        });

        timeline.to(mainContentRef, {
          alpha: 1,
          duration: 0.4,
          ease: "power2.out"
        }, "-=0.15");
      }
    });
  });

  // Function to load cached data
  const loadCachedData = () => {
    console.log("🔍 Checking for cached data...");

    let hasAnyCachedData = false;

    // Try to load hero movies from cache
    const cachedHeroMovies = cache.get(CacheKeys.heroMovies());
    if (cachedHeroMovies && cachedHeroMovies.length > 0) {
      console.log("💾 Loading hero movies from cache");
      setHeroMovies(cachedHeroMovies);
      setIsLoadingHero(false);
      hasAnyCachedData = true;
    }

    // Try to load channels from cache - ALWAYS load if available
    const cachedChannels = cache.get(CacheKeys.channels("all"));
    if (cachedChannels && cachedChannels.length > 0) {
      console.log("💾 Loading channels from cache");
      setLiveChannels(cachedChannels);
      setIsLoadingChannels(false);
      hasAnyCachedData = true;
    }

    // Try to load movies from cache - ALWAYS load if available
    const cachedMovies = cache.get(CacheKeys.homeMovies());
    if (cachedMovies && cachedMovies.length > 0) {
      console.log("💾 Loading home movies from cache");
      setMovies(cachedMovies);
      setIsLoadingMovies(false);
      hasAnyCachedData = true;
    }

    // Only set this flag if we have ALL cached data
    if (cachedHeroMovies && cachedChannels && cachedMovies) {
      setDataLoadedFromCache(true);
    }
  };

  // All channels provider (sorted by num, no category filter)
  const channelsData = createMemo(() => {
    const cacheKey = CacheKeys.channels("all");
    const cachedProvider = cache.get(cacheKey);
    if (cachedProvider) {
      console.log("💾 Using cached channels provider");
      return cachedProvider;
    }
    const provider = createInfiniteScroll(livetvChannels("all"));
    cache.set(cacheKey, provider, CacheTTL.channels);
    return provider;
  });

  const movieCategories = createMemo(() => {
    console.log("🔄 Creating movieCategories memo");

    const cachedCategories = cache.get(CacheKeys.movieCategories());
    if (cachedCategories) {
      console.log("💾 Using cached movie categories");
      debugLog("Cached Movie Categories", cachedCategories);
      return cachedCategories;
    }

    console.log("📡 Calling moviesCategory API...");
    const result = createInfiniteScroll(moviesCategory("all"));
    console.log("🎬 movieCategories result:", result);
    debugLog("Movie Categories Pages", result?.pages?.());

    cache.set(CacheKeys.movieCategories(), result, CacheTTL.categories);
    return result;
  });

  // Effect to get first movie category (skip "Last Added")
  createEffect(() => {
    console.log("🔍 Checking movie categories...");
    const movieCats = movieCategories();

    if (movieCats && movieCats.pages) {
      const pages = movieCats.pages();

      if (pages && pages.length > 0) {
        const firstRealCategory = pages.find(cat => {
          const catId = cat.category_id || cat.categoryID;
          return catId !== "last_added";
        });

        if (firstRealCategory) {
          console.log("✅ First Movie Category (skipped Last Added):", firstRealCategory);
          setFirstMovieCategory(firstRealCategory);
        } else if (pages.length > 1) {
          setFirstMovieCategory(pages[1]);
        }
      }
    }
  });

  // Effect to extract channels from the provider WITH CACHING
  createEffect(() => {
    if (dataLoadedFromCache()) {
      console.log("⏭️ Skipping channels fetch, using cached data");
      return;
    }

    const provider = channelsData();

    if (provider && provider.pages) {
      const pages = provider.pages();

      console.log("channels dataaa", pages.length);

      if (pages && pages.length > 0) {
        const sorted = [...pages].sort((a: any, b: any) => (a.num || 0) - (b.num || 0));
        const channelsSlice = sorted.slice(0, 10);
        setLiveChannels(channelsSlice);
        setIsLoadingChannels(false);

        // Cache the channels slice
        cache.set(CacheKeys.channels("all"), channelsSlice, CacheTTL.channels);
      } else {
        setIsLoadingChannels(false);
      }
    }
  });

  // Create memoized movies provider WITH CACHING
  const moviesData = createMemo(() => {
    const category = firstMovieCategory();
    if (!category) return null;

    const categoryId = category.category_id || category.categoryID;
    const cacheKey = CacheKeys.movies(categoryId);

    // Check cache first
    const cachedProvider = cache.get(cacheKey);
    if (cachedProvider) {
      console.log("💾 Using cached movies provider");
      return cachedProvider;
    }

    const provider = createInfiniteScroll(moviesProvider("all", categoryId));

    // Cache the provider
    cache.set(cacheKey, provider, CacheTTL.movies);

    return provider;
  });

  // Effect to extract movies from the provider AND fetch hero movie details WITH CACHING
  createEffect(() => {
    if (dataLoadedFromCache()) {
      console.log("⏭️ Skipping movies fetch, using cached data");
      return;
    }

    const provider = moviesData();

    if (provider && provider.pages) {
      const pages = provider.pages();

      if (pages && pages.length > 0) {
        // Check if we have cached home movies (Popular Movies row)
        const cachedHomeMovies = cache.get(CacheKeys.homeMovies());

        if (cachedHomeMovies && cachedHomeMovies.length > 0) {
          console.log("💾 Using cached home movies (2-hour cache)");
          setMovies(cachedHomeMovies);
          setIsLoadingMovies(false);
        } else {
          // Randomly select 10 movies for the home page
          console.log("🎲 Randomly selecting 10 movies for Popular Movies row...");
          const availableMovies = [...pages]; // Clone the array
          const selectedHomeMovies = [];

          // Fisher-Yates shuffle and take first 10
          for (let i = 0; i < Math.min(10, availableMovies.length); i++) {
            const randomIndex = Math.floor(Math.random() * (availableMovies.length - i)) + i;
            // Swap
            [availableMovies[i], availableMovies[randomIndex]] = [availableMovies[randomIndex], availableMovies[i]];
            selectedHomeMovies.push(availableMovies[i]);
          }

          console.log(`✅ Selected ${selectedHomeMovies.length} random movies (will cache for 2 hours)`);

          // Cache the random selection for 2 hours
          cache.set(CacheKeys.homeMovies(), selectedHomeMovies, CacheTTL.homeMovies);

          setMovies(selectedHomeMovies);
          setIsLoadingMovies(false);
        }

        // Fetch detailed info for randomly selected movies for hero carousel
        const fetchHeroMovies = async () => {
          // Check if we already have cached hero movies
          const cachedHeroMovies = cache.get(CacheKeys.heroMovies());
          if (cachedHeroMovies && cachedHeroMovies.length > 0) {
            console.log("💾 Using cached hero movies (2-hour cache)");
            setHeroMovies(cachedHeroMovies);
            setIsLoadingHero(false);
            return;
          }

          console.log("🎯 Fetching NEW random hero movies...");
          setIsLoadingHero(true);

          // Randomly select 5 movies from available pages for hero
          const availableMovies = [...pages]; // Clone the array
          const selectedMovies = [];

          // Fisher-Yates shuffle and take first 5
          for (let i = 0; i < Math.min(5, availableMovies.length); i++) {
            const randomIndex = Math.floor(Math.random() * (availableMovies.length - i)) + i;
            // Swap
            [availableMovies[i], availableMovies[randomIndex]] = [availableMovies[randomIndex], availableMovies[i]];
            selectedMovies.push(availableMovies[i]);
          }

          console.log(`🎲 Randomly selected ${selectedMovies.length} movies for hero carousel`);

          const heroMoviesData = [];

          for (const movie of selectedMovies) {
            const movieData = movie.data || movie;
            const streamId = movieData.stream_id || movie.entityInfo?.id;

            if (streamId) {
              const info = await fetchMovieInfo(streamId);

              if (info && info.info) {
                heroMoviesData.push({
                  id: streamId,
                  title: info.movie_data?.name || movieData.name || "Unknown",
                  genre: info.info.genre || "N/A",
                  year: info.info.releasedate ? info.info.releasedate.split("-")[0] : "N/A",
                  duration: formatRuntime(info.info.runtime || info.info.episode_run_time),
                  description: info.info.description || info.info.plot || "",
                  backdrop: info.info.backdrop_path?.[0] || movieData.stream_icon || "https://image.tmdb.org/t/p/original/8I37NtDffNV7AZlDa7uDvvqhovU.jpg",
                  rating: info.info.rating || "N/A",
                  streamId: streamId,
                  categoryId: movieData.category_id || movie.entityInfo?.category_id
                });
              }
            }
          }

          console.log(`✅ Loaded ${heroMoviesData.length} random hero movies (will cache for 2 hours)`);

          // Cache the hero movies for 2 hours
          cache.set(CacheKeys.heroMovies(), heroMoviesData, CacheTTL.heroMovies);

          setHeroMovies(heroMoviesData);
          setIsLoadingHero(false);
        };

        fetchHeroMovies();
      } else {
        setIsLoadingMovies(false);
        setIsLoadingHero(false);
      }
    }
  });

  return (
    <View
      color={hexColor("#000000")}
      style={{ width: 1920, height: 1080 }}
      onEscape={handleBackPress}
    >
      {/* Full screen black background */}
      <View
        width={1920}
        height={1080}
        color={hexColor("#000000")}
        style={{ position: "absolute", zIndex: 0 }}
      />

      {/* ── Exit Toast ── */}
      <Show when={showExitToast()}>
        <View
          x={60} y={60}
          width={500} height={100}
          color={hexColor("#000000")}
          alpha={0.9}
          borderRadius={15}
          border={{ width: 2, color: hexColor("#FFFFFF") }}
          style={{ zIndex: 9999 }}
        >
          <View x={0} y={0} width={500} height={100} color={hexColor("#FF5722")} alpha={0.2} borderRadius={15} />
          <Text x={100} y={22} fontSize={28} color={hexColor("#FFFFFF")} style={{ fontWeight: "bold", textAlign: "center" }}>
            Press Back Again to Exit
          </Text>
          <Text x={120} y={60} fontSize={18} color={hexColor("#CCCCCC")} style={{ textAlign: "center" }}>
            Press once more to close the app
          </Text>
        </View>
      </Show>

      {/* Loading Screen */}
      <Show when={showLoading()}>
        <View
          ref={loadingScreenRef}
          width={1920}
          height={1080}
          color={hexColor("#000000")}
          alpha={1}
          style={{ zIndex: 1000 }}
        >
          <View x={900} y={400}>
            <LoadingIndicator />
          </View>
        </View>
      </Show>

      {/* Main Content */}
      <View ref={mainContentRef} alpha={0}>
        {/* Top Navigation Bar */}
        <View x={580} y={40} style={{ zIndex: 100 }}>
          <Row
            ref={topNavRef}
            autofocus
            gap={20}
            scroll="none"
            onSelectedChanged={(index: number) => setTopNavSelectedIndex(index)}
            onFocus={() => setIsTopNavFocused(true)}
            onBlur={() => setIsTopNavFocused(false)}
            onDown={() => {
              // If Movies/Services/Search is open, Down should land on its
              // content, not on Home's own (hidden, behind the overlay)
              // content column.
              if (showMoviesPanel() && moviesPanelRef.focusEntry) {
                moviesPanelRef.focusEntry();
              } else if (showServicesPanel() && servicesPanelRef.focusEntry) {
                servicesPanelRef.focusEntry();
              } else if (showSearchPanel() && searchPanelRef.focusEntry) {
                searchPanelRef.focusEntry();
              } else if (contentColumnRef) {
                contentColumnRef.setFocus();
              }
            }}
          >
            <For each={NAV_ITEMS}>
              {(item, index) => (
                <Button
                  ref={(el: any) => {
                    if (item.label === "Settings") settingsNavButtonRef = el;
                    if (item.label === "Movies") moviesNavButtonRef = el;
                    if (item.label === "Services") servicesNavButtonRef = el;
                  }}
                  onEnter={item.onEnter}
                  style={{
                    height: 60,
                    width: 120,
                    // fontSize: 36,
                    borderRadius: 30,
                    color: !isTopNavFocused() && index() === topNavSelectedIndex()
                      ? hexColor("#3A3A3A")
                      : hexColor("#00000000"),
                    focus: {
                      color: hexColor("#ffffff"),
                      textColor: hexColor("#000000")
                    },
                  }}
                >
                  {item.label}
                </Button>
              )}
            </For>

            {/* Search — icon-only, so it's a plain View (Button always
                wraps its children in a Text node, which can't hold an
                icon). After Settings, last in the header row. Shown
                inline below the header instead of navigating away — see
                the SmartSearchPanel overlay near the end of this
                component's JSX. */}
            <View
              ref={(el: any) => { searchNavButtonRef = el; }}
              onEnter={() => {
                setShowMoviesPanel(false);
                setShowServicesPanel(false);
                setShowSettings(false);
                setShowSearchPanel(true);
              }}
              style={{
                width: 60,
                height: 60,
                borderRadius: 30,
                color: hexColor("#00000000"),
                alpha: 0.6,
                border: { width: 2, color: hexColor("#ffffff") },
                focus: {
                  alpha: 0.9,
                  border: { width: 3, color: hexColor("#ffffff") }
                }
              }}
            >
              <View x={16} y={16} src="https://i.ibb.co/nNhBcw8M/search.png" width={28} height={28} />
            </View>
          </Row>
        </View>

        {/* Main Content Column */}
        <View y={130} clipping>
          <Column
            ref={contentColumnRef}
            scroll="always"
            gap={100}
            onUp={() => {
              if (topNavRef) topNavRef.setFocus();
            }}
          >
            {/* 1. Hero Carousel Row - Dynamic Data */}
            <View x={150} height={580} forwardFocus={0}>
              <Show
                when={!isLoadingHero() && heroMovies().length > 0}
                fallback={
                  <View x={400} y={300}>
                    <Text fontSize={24} color={hexColor("#B0B0B0")}>
                      {isLoadingHero() ? "Loading featured movies..." : "No movies available"}
                    </Text>
                  </View>
                }
              >
                <Row
                  ref={heroRowRef}
                  gap={270}
                  scroll="always"
                  autofocus
                  onSelectedChanged={(index: number) => setHeroSelectedIndex(index)}
                  onUp={() => {
                    if (topNavRef) topNavRef.setFocus();
                  }}
                >
                  <For each={heroMovies()}>
                    {(item) => (
                      <View
                        onEnter={() => {
                          if (item.streamId && item.categoryId) {
                            handleNavigate(`/entity/movie/${item.streamId}/${item.categoryId}`);
                          }
                        }}
                        style={{
                          width: 1600,
                          height: 610,
                          borderRadius: 45,
                          border: { width: 8, color: hexColor("#1E1E1E") },
                          focus: { border: { width: 8, color: hexColor("#b2b1b1") } }
                        }}
                      >
                        <View
                          src={fixImageUrl(item.backdrop)}
                          width={1040}
                          height={600}
                          x={555}
                          y={5}
                          borderRadius={45}
                        >
                          <View
                            src="assets/hero-gradient-mask.png"
                            borderRadius={45}
                            width={1590}
                            height={600}
                            x={-550}
                          />
                        </View>

                        <View x={60} y={220} skipFocus>
                          <Text
                            skipFocus
                            fontSize={24}
                            color={hexColor("#B0B0B0")}
                            style={{ lineHeight: 1.5 }}
                          >
                            {item.genre} • {item.year} • {item.duration}
                          </Text>

                          <For each={splitHeroTitle(item.title)}>
                            {(line, lineIndex) => (
                              <Text
                                skipFocus
                                y={55 + lineIndex() * 65}
                                fontSize={50}
                                color={hexColor("#FFFFFF")}
                                contain="width"
                                width={1100}
                                style={{ fontWeight: "normal", lineHeight: 1.2, maxLines: 1 }}
                              >
                                {line}
                              </Text>
                            )}
                          </For>

                          <Show when={item.description}>
                            <Text
                              skipFocus
                              y={splitHeroTitle(item.title).length > 1 ? 180 : 135}
                              fontSize={26}
                              color={hexColor("#B0B0B0")}
                              style={{
                                lineHeight: 32,
                                width: 1200,
                                maxLines: 2,
                                contain: "width"
                              }}
                            >
                              {item.description}
                            </Text>
                          </Show>
                        </View>

                        {/* Watch now button (card Enter opens the movie) */}
                        <View
                          skipFocus
                          x={60}
                          y={500}
                          width={220}
                          height={64}
                          borderRadius={32}
                          color={hexColor("#E8E6EE")}
                        >
                          <View
                            skipFocus
                            src="assets/play-icon.png"
                            x={25}
                            y={22}
                            width={24}
                            height={24}
                          />
                          <Text
                            skipFocus
                            x={58}
                            y={22}
                            fontSize={25}
                            color={hexColor("#1A1A1A")}
                          >
                            Watch now
                          </Text>
                        </View>
                      </View>
                    )}
                  </For>
                </Row>
              </Show>

              {/* Hero Carousel Pagination Dots */}
              <Show when={!isLoadingHero() && heroMovies().length > 1}>
                <View x={1440} y={548} skipFocus>
                  <For each={heroMovies()}>
                    {(_, index) => (
                      <View
                        skipFocus
                        x={index() * 22}
                        width={12}
                        height={12}
                        borderRadius={6}
                        color={hexColor("#FFFFFF")}
                        alpha={index() === heroSelectedIndex() ? 1 : 0.35}
                      />
                    )}
                  </For>
                </View>
              </Show>
            </View>

            {/* 2. Live TV Channels Carousel Row */}
            <View x={80} height={360} forwardFocus={1}>
              <Text
                skipFocus
                fontSize={32}
                color={hexColor("#FFFFFF")}
                style={{ fontWeight: "bold" }}
              >
                Live TV Channels
              </Text>

              <View y={60} forwardFocus={0}>
                <Show
                  when={!isLoadingChannels() && liveChannels().length > 0}
                  fallback={
                    <View x={400}>
                      <Text fontSize={20} color={hexColor("#B0B0B0")}>
                        {isLoadingChannels() ? "Loading channels..." : "No channels available"}
                      </Text>
                    </View>
                  }
                >
                  <Row
                    ref={channelsRowRef}
                    gap={20}
                    scroll="always"
                    selected={0}
                    onUp={() => {
                      if (heroRowRef) heroRowRef.setFocus();
                    }}
                  >
                    <For each={liveChannels()}>
                      {(channel) => (
                        <ChannelCard
                          channel={channel}
                          onEnter={() => {
                            const streamId = channel.stream_id;
                            if (streamId) {
                              handleNavigate(`/livetv?num=${streamId}&isTrue=true`);
                              setIsPlaying(true);
                            }
                          }}
                        />
                      )}
                    </For>
                  </Row>
                </Show>
              </View>
            </View>

            {/* 3. Movies Carousel Row */}
            <View x={80} height={520} forwardFocus={1}>
              <Text
                skipFocus
                fontSize={32}
                color={hexColor("#FFFFFF")}
                style={{ fontWeight: "bold" }}
              >
                Popular Movies
              </Text>

              <View y={60} forwardFocus={0}>
                <Show
                  when={!isLoadingMovies() && movies().length > 0}
                  fallback={
                    <View x={400}>
                      <Text fontSize={20} color={hexColor("#B0B0B0")}>
                        {isLoadingMovies() ? "Loading movies..." : "No movies available"}
                      </Text>
                    </View>
                  }
                >
                  <Row
                    ref={moviesRowRef}
                    gap={20}
                    scroll="always"
                    onUp={() => {
                      if (channelsRowRef) channelsRowRef.setFocus();
                    }}
                  >
                    <For each={movies()}>
                      {(movie) => (
                        <MovieCard
                          movie={movie}
                          fallbackCategoryId={firstMovieCategory()?.category_id || firstMovieCategory()?.categoryID}
                          onOpen={(streamId, categoryId) => handleNavigate(`/entity/movie/${streamId}/${categoryId}`)}
                        />
                      )}
                    </For>
                  </Row>
                </Show>
              </View>
            </View>

          </Column>
        </View>

        {/* Settings/Movies/Services — shown inline over the content, below
            the header (zIndex 100 above), instead of navigating to their
            own full-page routes. Rendered as siblings, not nested inside
            one another, so at most one is ever an ancestor of the focused
            element — see the SettingsPanel Escape-bubbling note below. */}
        <Show when={showSettings()}>
          <View style={{ position: "absolute", zIndex: 50 }}>
            <SettingsPanel
              onEscape={() => {
                setShowSettings(false);
                settingsNavButtonRef?.setFocus?.();
              }}
              onUp={() => topNavRef?.setFocus?.()}
            />
          </View>
        </Show>

        <Show when={showMoviesPanel()}>
          <View style={{ position: "absolute", zIndex: 50 }}>
            <MoviesPanel
              onEscape={() => {
                setShowMoviesPanel(false);
                moviesNavButtonRef?.setFocus?.();
              }}
              onUp={() => topNavRef?.setFocus?.()}
              panelRef={moviesPanelRef}
            />
          </View>
        </Show>

        <Show when={showServicesPanel()}>
          <View style={{ position: "absolute", zIndex: 50 }}>
            <ServicesPanel
              onEscape={() => {
                setShowServicesPanel(false);
                servicesNavButtonRef?.setFocus?.();
              }}
              onUp={() => topNavRef?.setFocus?.()}
              panelRef={servicesPanelRef}
            />
          </View>
        </Show>

        <Show when={showSearchPanel()}>
          <View style={{ position: "absolute", zIndex: 50 }}>
            <SmartSearchPanel
              onEscape={() => {
                setShowSearchPanel(false);
                searchNavButtonRef?.setFocus?.();
              }}
              onUp={() => topNavRef?.setFocus?.()}
              panelRef={searchPanelRef}
            />
          </View>
        </Show>
      </View>

    </View>
  );
};

export default ResidentialHome;